import React from 'react';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import usePageLayout from '@hooks/usePageLayout/usePageLayout';
import type { PageLayout } from '@hooks/usePageLayout/pageLayout';

/** A mini picture of the layout: one block for the list, two side by side, or two stacked. */
const MiniLayout: React.FC<{ layout: PageLayout; selected: boolean }> = ({ layout, selected }) => {
  const first = selected ? 'bg-content-brand/40' : 'bg-content-gr2';
  const second = selected ? 'bg-content-brand/15' : 'bg-content-quaternary';
  return (
    <span className={clsx('flex w-full gap-1 rounded bg-base-base p-1.5', layout === 'horizontal' ? 'flex-col' : 'flex-row')}>
      {layout === 'list' && <span className={clsx('h-11 w-full rounded-sm', first)} />}
      {layout === 'vertical' && (
        <>
          <span className={clsx('h-11 flex-1 rounded-sm', first)} />
          <span className={clsx('h-11 flex-1 rounded-sm', second)} />
        </>
      )}
      {layout === 'horizontal' && (
        <>
          <span className={clsx('h-5 w-full rounded-sm', first)} />
          <span className={clsx('h-5 w-full rounded-sm', second)} />
        </>
      )}
    </span>
  );
};

/**
 * "Rozložení stránek" in the profile menu (Figma 21940:63130): list only, list and detail side by side,
 * or list above the detail. The choice applies on every page that supports a split.
 */
const PageLayoutTiles: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { layout, setLayout } = usePageLayout();
  const options: { value: PageLayout; label: string }[] = [
    { value: 'list', label: tr('PageLayout.list', 'List') },
    { value: 'vertical', label: tr('PageLayout.vertical', 'List and detail side by side') },
    { value: 'horizontal', label: tr('PageLayout.horizontal', 'List above the detail') },
  ];

  return (
    <div className="flex w-[270px] flex-col gap-2 px-4 pt-3 pb-2" data-testid="page-layout-tiles">
      <span className="text-sm font-medium text-content-primary">{tr('PageLayout.heading', 'Page layout')}</span>
      <div className="flex gap-2" role="radiogroup" aria-label={tr('PageLayout.heading', 'Page layout')}>
        {options.map(option => {
          const selected = option.value === layout;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={option.label}
              title={option.label}
              onClick={event => {
                // The tiles sit inside a menu row; picking one must not navigate or close anything.
                event.stopPropagation();
                setLayout(option.value);
              }}
              className={clsx(
                'flex flex-1 cursor-pointer rounded-md border-solid px-1.5 py-2',
                selected ? 'border-[1.5px] border-content-brand bg-complementary-brandLight' : 'border border-content-gr2 bg-transparent hover:border-content-brand',
              )}
              data-testid={`page-layout-${option.value}`}
            >
              <MiniLayout layout={option.value} selected={selected} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PageLayoutTiles;
